import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banknote, Building2, CalendarClock, CheckCircle2, Handshake,
  Loader2, Pencil, Plus, Power, ShieldCheck, XCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { adminSupabase } from '@/integrations/supabase/adminClient';
import { useAdminScope } from '@/hooks/useAdminScope';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { useTranslation } from 'react-i18next';
import {
  calculateEnterpriseContractTotals,
  effectiveEnterpriseDealStatus,
  enterpriseDealActivationBlocker,
  serializeEnterpriseServices,
  validateEnterpriseServices,
  type EnterpriseServiceDraft,
} from '@/lib/enterpriseDeals';

interface EnterpriseDeal {
  id: string;
  org_id: string;
  deal_name: string;
  deal_type: string;
  base_plan_id: string | null;
  plan_id: string | null;
  status: string;
  payment_status: string | null;
  payment_terms: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  payment_received_at: string | null;
  quoted_subtotal_sar: number | null;
  vat_rate: number | null;
  vat_amount_sar: number | null;
  total_contract_value_sar: number | null;
  billing_cycle: string | null;
  grace_period_days: number | null;
  contract_start: string | null;
  contract_end: string | null;
  account_manager: string | null;
  internal_notes: string | null;
  discount_percent: number | null;
  discount_fixed_sar: number | null;
  subscription_id: string | null;
  created_at: string;
}

interface OrganizationOption {
  id: string;
  name: string;
  contact_email: string | null;
  lifecycle_status: string | null;
}

interface PlanOption {
  id: string;
  plan_name: string;
  plan_key: string;
}

interface ServiceOption {
  id: string;
  service_key: string;
  service_name: string;
  category: string | null;
}

const emptyDealForm = () => {
  const start = new Date();
  const end = new Date(start);
  end.setFullYear(end.getFullYear() + 1);
  end.setDate(end.getDate() - 1);
  return {
    id: '', org_id: '', base_plan_id: '', deal_name: '', deal_type: 'custom',
    contract_start: start.toISOString().slice(0, 10),
    contract_end: end.toISOString().slice(0, 10),
    billing_cycle: 'yearly', grace_period_days: '0', quoted_subtotal_sar: '',
    vat_rate: '15', discount_percent: '', discount_fixed_sar: '',
    payment_terms: 'prepaid', account_manager: '', internal_notes: '',
  };
};

function money(value: number | null | undefined) {
  return `${Number(value ?? 0).toLocaleString('en-SA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;
}

export default function SAEnterpriseDeals() {
  const { toast } = useToast();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const { canWrite } = useAdminScope();
  const isReadOnly = !canWrite('enterprise-deals');

  const [dealOpen, setDealOpen] = useState(false);
  const [paymentDeal, setPaymentDeal] = useState<EnterpriseDeal | null>(null);
  const [action, setAction] = useState<{ type: 'activate' | 'cancel'; deal: EnterpriseDeal } | null>(null);
  const [dealForm, setDealForm] = useState(emptyDealForm);
  const [serviceDrafts, setServiceDrafts] = useState<EnterpriseServiceDraft[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', reference: '', received_at: '', reason: '' });
  const [actionReason, setActionReason] = useState('');

  const { data: deals = [], isLoading } = useQuery<EnterpriseDeal[]>({
    queryKey: ['admin-enterprise-deals'],
    queryFn: async () => {
      const { data, error } = await adminSupabase
        .from('enterprise_deals')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as EnterpriseDeal[];
    },
  });

  const { data: organizations = [] } = useQuery<OrganizationOption[]>({
    queryKey: ['enterprise-deal-organizations'],
    queryFn: async () => {
      const { data, error } = await adminSupabase
        .from('organizations')
        .select('id,name,contact_email,lifecycle_status')
        .order('name');
      if (error) throw error;
      return (data ?? []) as OrganizationOption[];
    },
  });

  const { data: basePlans = [] } = useQuery<PlanOption[]>({
    queryKey: ['enterprise-deal-base-plans'],
    queryFn: async () => {
      const { data, error } = await adminSupabase
        .from('plans')
        .select('id,plan_name,plan_key')
        .eq('is_active', true)
        .eq('platform', 'forkhire')
        .is('deleted_at', null)
        .neq('plan_type', 'internal')
        .order('display_order');
      if (error) throw error;
      return (data ?? []) as PlanOption[];
    },
  });

  const { data: services = [] } = useQuery<ServiceOption[]>({
    queryKey: ['enterprise-deal-services'],
    queryFn: async () => {
      const { data, error } = await adminSupabase
        .from('services')
        .select('id,service_key,service_name,category')
        .eq('is_active', true)
        .eq('platform', 'forkhire')
        .order('display_order');
      if (error) throw error;
      return (data ?? []) as ServiceOption[];
    },
  });

  const orgMap = useMemo(() => new Map(organizations.map((org) => [org.id, org])), [organizations]);
  const totals = calculateEnterpriseContractTotals(dealForm.quoted_subtotal_sar, dealForm.vat_rate);

  async function loadServicesFromPlan(planId: string) {
    setServicesLoading(true);
    try {
      const { data: mappings, error } = await adminSupabase
        .from('plan_services')
        .select('id,service_id,is_enabled,usage_limit,limit_type')
        .eq('plan_id', planId);
      if (error) throw error;

      const mappingIds = (mappings ?? []).map((mapping) => mapping.id);
      const rules = mappingIds.length
        ? await adminSupabase
            .from('plan_service_rules')
            .select('plan_service_id,rule_key,rule_value')
            .in('plan_service_id', mappingIds)
        : { data: [], error: null };
      if (rules.error) throw rules.error;

      const mappingByService = new Map((mappings ?? []).map((mapping) => [mapping.service_id, mapping]));
      const ruleByKey = new Map((rules.data ?? []).map((rule) => [`${rule.plan_service_id}:${rule.rule_key}`, rule.rule_value]));
      setServiceDrafts(services.map((service) => {
        const mapping = mappingByService.get(service.id);
        const limitType = service.service_key === 'candidate_pipeline'
          ? 'total'
          : ((mapping?.limit_type ?? 'monthly') as EnterpriseServiceDraft['limit_type']);
        return {
          service_id: service.id,
          service_key: service.service_key,
          service_name: service.service_name,
          is_enabled: mapping?.is_enabled ?? false,
          usage_limit: mapping?.usage_limit == null ? '' : String(mapping.usage_limit),
          limit_type: limitType,
          base_shortlist_pass_limit: mapping ? String(ruleByKey.get(`${mapping.id}:base_shortlist_pass_limit`) ?? '') : '',
          max_replacement_pass_limit: mapping ? String(ruleByKey.get(`${mapping.id}:max_replacement_pass_limit`) ?? '') : '',
        };
      }));
    } finally {
      setServicesLoading(false);
    }
  }

  const saveDeal = useMutation({
    mutationFn: async () => {
      const serviceError = validateEnterpriseServices(serviceDrafts);
      if (serviceError) throw new Error(serviceError);
      if (!dealForm.org_id || !dealForm.base_plan_id || !dealForm.deal_name.trim()) {
        throw new Error(t('admin.enterpriseDeals.errors.missingRequired'));
      }
      if (!dealForm.contract_start || !dealForm.contract_end || dealForm.contract_end <= dealForm.contract_start) {
        throw new Error(t('admin.enterpriseDeals.errors.invalidDates'));
      }

      const { data, error } = await adminSupabase.rpc('admin_save_enterprise_deal', {
        _deal_id: dealForm.id || null,
        _payload: {
          ...dealForm,
          services: serializeEnterpriseServices(serviceDrafts),
        },
      });
      if (error) throw error;
      if (data && typeof data === 'object' && !Array.isArray(data) && data.success === false) {
        throw new Error(typeof data.error === 'string' ? data.error : t('admin.enterpriseDeals.errors.saveFailed'));
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enterprise-deals'] });
      setDealOpen(false);
      toast({ title: t('admin.enterpriseDeals.toasts.draftSaved') });
    },
    onError: (error: Error) => toast({ title: t('admin.enterpriseDeals.toasts.saveBlocked'), description: error.message, variant: 'destructive' }),
  });

  const confirmPayment = useMutation({
    mutationFn: async () => {
      if (!paymentDeal) throw new Error(t('admin.enterpriseDeals.errors.selectDeal'));
      const { data, error } = await adminSupabase.rpc('admin_confirm_enterprise_deal_payment', {
        _deal_id: paymentDeal.id,
        _amount_sar: Number(paymentForm.amount),
        _payment_reference: paymentForm.reference,
        _received_at: paymentForm.received_at ? new Date(paymentForm.received_at).toISOString() : null,
        _reason: paymentForm.reason,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enterprise-deals'] });
      setPaymentDeal(null);
      toast({ title: t('admin.enterpriseDeals.toasts.transferConfirmed'), description: t('admin.enterpriseDeals.toasts.transferConfirmedDescription') });
    },
    onError: (error: Error) => toast({ title: t('admin.enterpriseDeals.toasts.paymentBlocked'), description: error.message, variant: 'destructive' }),
  });

  const lifecycleMutation = useMutation({
    mutationFn: async () => {
      if (!action) throw new Error(t('admin.enterpriseDeals.errors.selectAction'));
      const { data, error } = action.type === 'activate'
        ? await adminSupabase.rpc('admin_activate_enterprise_deal', {
          _deal_id: action.deal.id,
          _reason: actionReason,
        })
        : await adminSupabase.rpc('admin_cancel_enterprise_deal', {
          _deal_id: action.deal.id,
          _reason: actionReason,
        });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-enterprise-deals'] });
      queryClient.invalidateQueries({ queryKey: ['sa-all-subscriptions'] });
      queryClient.invalidateQueries({ queryKey: ['sa-organizations'] });
      const completed = action?.type;
      setAction(null);
      setActionReason('');
      toast({
        title: completed === 'activate' ? t('admin.enterpriseDeals.toasts.activated') : t('admin.enterpriseDeals.toasts.deactivated'),
        description: completed === 'activate'
          ? t('admin.enterpriseDeals.toasts.activatedDescription')
          : t('admin.enterpriseDeals.toasts.deactivatedDescription'),
      });
    },
    onError: (error: Error) => toast({ title: t('admin.enterpriseDeals.toasts.actionBlocked'), description: error.message, variant: 'destructive' }),
  });

  function openNewDeal() {
    setDealForm(emptyDealForm());
    setServiceDrafts(services.map((service) => ({
      service_id: service.id,
      service_key: service.service_key,
      service_name: service.service_name,
      is_enabled: false,
      usage_limit: '',
      limit_type: service.service_key === 'candidate_pipeline' ? 'total' : 'monthly',
      base_shortlist_pass_limit: '',
      max_replacement_pass_limit: '',
    })));
    setDealOpen(true);
  }

  async function openEditDeal(deal: EnterpriseDeal) {
    setDealForm({
      id: deal.id,
      org_id: deal.org_id,
      base_plan_id: deal.base_plan_id ?? '',
      deal_name: deal.deal_name,
      deal_type: deal.deal_type,
      contract_start: deal.contract_start?.slice(0, 10) ?? '',
      contract_end: deal.contract_end?.slice(0, 10) ?? '',
      billing_cycle: deal.billing_cycle ?? 'yearly',
      grace_period_days: String(deal.grace_period_days ?? 0),
      quoted_subtotal_sar: String(deal.quoted_subtotal_sar ?? ''),
      vat_rate: String(deal.vat_rate ?? 15),
      discount_percent: String(deal.discount_percent ?? ''),
      discount_fixed_sar: String(deal.discount_fixed_sar ?? ''),
      payment_terms: deal.payment_terms ?? 'prepaid',
      account_manager: deal.account_manager ?? '',
      internal_notes: deal.internal_notes ?? '',
    });
    setDealOpen(true);
    if (deal.plan_id) await loadServicesFromPlan(deal.plan_id);
  }

  function openPayment(deal: EnterpriseDeal) {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    setPaymentForm({
      amount: String(deal.total_contract_value_sar ?? ''),
      reference: '',
      received_at: now.toISOString().slice(0, 16),
      reason: '',
    });
    setPaymentDeal(deal);
  }

  return (
    <div className="mx-auto w-full max-w-[1500px] space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">{t('admin.enterpriseDeals.contractsTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('admin.enterpriseDeals.contractsSubtitle')}
          </p>
        </div>
        {!isReadOnly && (
          <Button className="gap-2" onClick={openNewDeal}>
            <Plus className="h-4 w-4" /> {t('admin.enterpriseDeals.newEnterpriseDeal')}
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardContent className="flex items-center gap-3 p-5"><Handshake className="h-5 w-5 text-primary" /><div><p className="text-2xl font-bold">{deals.length}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.totalContracts')}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-3 p-5"><Power className="h-5 w-5 text-emerald-600" /><div><p className="text-2xl font-bold">{deals.filter((deal) => effectiveEnterpriseDealStatus(deal) === 'active').length}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.active')}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-3 p-5"><Banknote className="h-5 w-5 text-amber-600" /><div><p className="text-2xl font-bold">{deals.filter((deal) => deal.status === 'draft' && deal.payment_status === 'pending').length}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.awaitingTransfer')}</p></div></CardContent></Card>
        <Card><CardContent className="flex items-center gap-3 p-5"><CalendarClock className="h-5 w-5 text-sky-600" /><div><p className="text-2xl font-bold">{deals.filter((deal) => effectiveEnterpriseDealStatus(deal) === 'expired').length}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.expired')}</p></div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" /> {t('admin.enterpriseDeals.governedDeals')}</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : deals.length === 0 ? (
            <div className="py-12 text-center"><Building2 className="mx-auto mb-3 h-8 w-8 text-muted-foreground" /><p className="font-medium">{t('admin.enterpriseDeals.noDealsYet')}</p><p className="text-sm text-muted-foreground">{t('admin.enterpriseDeals.noDealsHint')}</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>{t('admin.enterpriseDeals.customerDeal')}</TableHead><TableHead>{t('admin.enterpriseDeals.contract')}</TableHead><TableHead>{t('admin.enterpriseDeals.value')}</TableHead>
                  <TableHead>{t('admin.enterpriseDeals.payment')}</TableHead><TableHead>{t('admin.enterpriseDeals.statusLabel')}</TableHead><TableHead className="text-end">{t('admin.enterpriseDeals.actions')}</TableHead>
                </TableRow></TableHeader>
                <TableBody>{deals.map((deal) => {
                  const status = effectiveEnterpriseDealStatus(deal);
                  const blocker = enterpriseDealActivationBlocker(deal);
                  return (
                    <TableRow key={deal.id}>
                      <TableCell><p className="font-medium">{orgMap.get(deal.org_id)?.name ?? deal.org_id.slice(0, 8)}</p><p className="text-xs text-muted-foreground">{deal.deal_name} · {deal.deal_type}</p></TableCell>
                      <TableCell className="text-sm">{deal.contract_start && deal.contract_end ? <><p>{formatLocalizedDate(deal.contract_start, 'dd MMM yyyy', i18n.language)} – {formatLocalizedDate(deal.contract_end, 'dd MMM yyyy', i18n.language)}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.graceDaysAndCycle', { count: deal.grace_period_days ?? 0, cycle: deal.billing_cycle })}</p></> : '—'}</TableCell>
                      <TableCell><p className="font-medium">{money(deal.total_contract_value_sar)}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.includesVat', { amount: money(deal.vat_amount_sar) })}</p></TableCell>
                      <TableCell><Badge variant={deal.payment_status === 'confirmed' ? 'default' : 'secondary'}>{deal.payment_status ?? t('admin.enterpriseDeals.pending')}</Badge>{deal.payment_reference && <p className="mt-1 max-w-36 truncate text-xs text-muted-foreground">{t('admin.enterpriseDeals.reference')}: {deal.payment_reference}</p>}</TableCell>
                      <TableCell><Badge variant={status === 'active' ? 'default' : status === 'cancelled' || status === 'expired' ? 'destructive' : 'secondary'}>{status}</Badge></TableCell>
                      <TableCell><div className="flex justify-end gap-1">
                        {!isReadOnly && deal.status === 'draft' && deal.payment_status === 'pending' && <Button size="sm" variant="ghost" onClick={() => openEditDeal(deal)} title={t('admin.enterpriseDeals.editDraft')}><Pencil className="h-4 w-4" /></Button>}
                        {!isReadOnly && deal.status === 'draft' && deal.payment_status === 'pending' && <Button size="sm" variant="outline" className="gap-1" onClick={() => openPayment(deal)}><Banknote className="h-3.5 w-3.5" /> {t('admin.enterpriseDeals.confirmTransfer')}</Button>}
                        {!isReadOnly && deal.status === 'draft' && deal.payment_status === 'confirmed' && <Button size="sm" className="gap-1" disabled={!!blocker} title={blocker ?? t('admin.enterpriseDeals.activate')} onClick={() => { setActionReason(''); setAction({ type: 'activate', deal }); }}><Power className="h-3.5 w-3.5" /> {t('admin.enterpriseDeals.activate')}</Button>}
                        {!isReadOnly && ['draft', 'active'].includes(deal.status) && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { setActionReason(''); setAction({ type: 'cancel', deal }); }}>{deal.status === 'active' ? t('admin.enterpriseDeals.deactivate') : t('admin.enterpriseDeals.cancel')}</Button>}
                      </div></TableCell>
                    </TableRow>
                  );
                })}</TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dealOpen} onOpenChange={setDealOpen}>
        <DialogContent className="max-h-[92vh] max-w-5xl overflow-y-auto">
          <DialogHeader><DialogTitle>{dealForm.id ? t('admin.enterpriseDeals.editDealDraft') : t('admin.enterpriseDeals.createEnterpriseDeal')}</DialogTitle></DialogHeader>
          <div className="space-y-6 py-2">
            <section className="space-y-3"><h3 className="font-semibold">{t('admin.enterpriseDeals.customerPlanSection')}</h3><div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.customerOrganization')} *</Label><Select value={dealForm.org_id} disabled={!!dealForm.id} onValueChange={(value) => setDealForm((form) => ({ ...form, org_id: value }))}><SelectTrigger><SelectValue placeholder={t('admin.enterpriseDeals.selectCompany')} /></SelectTrigger><SelectContent>{organizations.filter((org) => !['deactivated', 'deleted', 'anonymized'].includes(org.lifecycle_status ?? '')).map((org) => <SelectItem key={org.id} value={org.id}>{org.name}{org.contact_email ? ` · ${org.contact_email}` : ''}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.basePlan')} *</Label><Select value={dealForm.base_plan_id} onValueChange={(value) => { setDealForm((form) => ({ ...form, base_plan_id: value })); loadServicesFromPlan(value); }}><SelectTrigger><SelectValue placeholder={t('admin.enterpriseDeals.startFromPlan')} /></SelectTrigger><SelectContent>{basePlans.map((plan) => <SelectItem key={plan.id} value={plan.id}>{plan.plan_name}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.dealName')} *</Label><Input value={dealForm.deal_name} onChange={(event) => setDealForm((form) => ({ ...form, deal_name: event.target.value }))} placeholder={t('admin.enterpriseDeals.dealNameExample')} /></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.dealType')}</Label><Select value={dealForm.deal_type} onValueChange={(value) => setDealForm((form) => ({ ...form, deal_type: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="custom">{t('admin.enterpriseDeals.custom')}</SelectItem><SelectItem value="volume">{t('admin.enterpriseDeals.volume')}</SelectItem><SelectItem value="strategic">{t('admin.enterpriseDeals.strategic')}</SelectItem></SelectContent></Select></div>
            </div></section>

            <section className="space-y-3"><div><h3 className="font-semibold">{t('admin.enterpriseDeals.servicesSection')}</h3><p className="text-sm text-muted-foreground">{t('admin.enterpriseDeals.servicesHint')}</p></div>
              {servicesLoading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div> : <div className="space-y-2">{serviceDrafts.map((service, index) => <div key={service.service_id} className="rounded-lg border p-3"><div className="flex flex-col gap-3 lg:flex-row lg:items-center"><div className="flex min-w-64 flex-1 items-center gap-3"><Switch checked={service.is_enabled} onCheckedChange={(checked) => setServiceDrafts((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, is_enabled: checked } : row))} /><div><p className="font-medium">{service.service_name}</p><p className="font-mono text-xs text-muted-foreground">{service.service_key}</p></div></div>{service.service_key === 'candidate_pipeline' ? <div className="grid flex-[2] gap-3 sm:grid-cols-2"><div><Label className="text-xs">{t('admin.enterpriseDeals.shortlistLimit')}</Label><Input type="number" min="1" disabled={!service.is_enabled} value={service.base_shortlist_pass_limit} onChange={(event) => setServiceDrafts((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, base_shortlist_pass_limit: event.target.value } : row))} /></div><div><Label className="text-xs">{t('admin.enterpriseDeals.replacementLimit')}</Label><Input type="number" min="0" disabled={!service.is_enabled} value={service.max_replacement_pass_limit} onChange={(event) => setServiceDrafts((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, max_replacement_pass_limit: event.target.value } : row))} /></div></div> : <div className="grid flex-[2] gap-3 sm:grid-cols-2"><div><Label className="text-xs">{t('admin.enterpriseDeals.allowanceType')}</Label><Select disabled={!service.is_enabled} value={service.limit_type} onValueChange={(value: EnterpriseServiceDraft['limit_type']) => setServiceDrafts((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, limit_type: value, usage_limit: value === 'unlimited' ? '' : row.usage_limit } : row))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">{t('admin.enterpriseDeals.monthly')}</SelectItem><SelectItem value="active">{t('admin.enterpriseDeals.activeAtOnce')}</SelectItem><SelectItem value="total">{t('admin.enterpriseDeals.contractTotal')}</SelectItem><SelectItem value="unlimited">{t('admin.enterpriseDeals.unlimited')}</SelectItem></SelectContent></Select></div><div><Label className="text-xs">{t('admin.enterpriseDeals.includedQuantity')}</Label><Input type="number" min="0" disabled={!service.is_enabled || service.limit_type === 'unlimited'} value={service.usage_limit} placeholder={service.limit_type === 'unlimited' ? t('admin.enterpriseDeals.unlimited') : '0'} onChange={(event) => setServiceDrafts((rows) => rows.map((row, rowIndex) => rowIndex === index ? { ...row, usage_limit: event.target.value } : row))} /></div></div>}</div></div>)}</div>}
            </section>

            <section className="space-y-3"><h3 className="font-semibold">{t('admin.enterpriseDeals.contractTermsSection')}</h3><div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.contractStart')} *</Label><Input type="date" value={dealForm.contract_start} onChange={(event) => setDealForm((form) => ({ ...form, contract_start: event.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.contractEnd')} *</Label><Input type="date" min={dealForm.contract_start || undefined} value={dealForm.contract_end} onChange={(event) => setDealForm((form) => ({ ...form, contract_end: event.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.paymentCadence')}</Label><Select value={dealForm.billing_cycle} onValueChange={(value) => setDealForm((form) => ({ ...form, billing_cycle: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="monthly">{t('admin.enterpriseDeals.monthly')}</SelectItem><SelectItem value="yearly">{t('admin.enterpriseDeals.annualPrepaid')}</SelectItem></SelectContent></Select></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.accessGraceDays')}</Label><Input type="number" min="0" max="90" value={dealForm.grace_period_days} onChange={(event) => setDealForm((form) => ({ ...form, grace_period_days: event.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.priceBeforeVat')} *</Label><Input type="number" min="0" step="0.01" value={dealForm.quoted_subtotal_sar} onChange={(event) => setDealForm((form) => ({ ...form, quoted_subtotal_sar: event.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.vatRate')}</Label><Input type="number" min="0" max="100" step="0.01" value={dealForm.vat_rate} onChange={(event) => setDealForm((form) => ({ ...form, vat_rate: event.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.paymentTerms')}</Label><Select value={dealForm.payment_terms} onValueChange={(value) => setDealForm((form) => ({ ...form, payment_terms: value }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="prepaid">{t('admin.enterpriseDeals.prepaid')}</SelectItem><SelectItem value="net_30">{t('admin.enterpriseDeals.net30')}</SelectItem><SelectItem value="net_60">{t('admin.enterpriseDeals.net60')}</SelectItem><SelectItem value="custom">{t('admin.enterpriseDeals.custom')}</SelectItem></SelectContent></Select></div>
              <div className="rounded-lg border bg-muted/40 p-3"><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.contractTotal')}</p><p className="text-lg font-bold">{money(totals.total)}</p><p className="text-xs text-muted-foreground">{t('admin.enterpriseDeals.vat')} {money(totals.vat)}</p></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>{t('admin.enterpriseDeals.accountManager')}</Label><Input value={dealForm.account_manager} onChange={(event) => setDealForm((form) => ({ ...form, account_manager: event.target.value }))} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>{t('admin.enterpriseDeals.internalContractNotes')}</Label><Textarea value={dealForm.internal_notes} onChange={(event) => setDealForm((form) => ({ ...form, internal_notes: event.target.value }))} /></div>
            </div></section>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDealOpen(false)}>{t('admin.enterpriseDeals.cancel')}</Button><Button onClick={() => saveDeal.mutate()} disabled={saveDeal.isPending || servicesLoading}>{saveDeal.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t('admin.enterpriseDeals.saveGovernedDraft')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentDeal} onOpenChange={(open) => !open && setPaymentDeal(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{t('admin.enterpriseDeals.confirmReceivedTransfer')}</DialogTitle></DialogHeader><div className="space-y-4 py-2"><div className="rounded-lg border bg-muted/40 p-3 text-sm"><p className="font-medium">{paymentDeal?.deal_name}</p><p className="text-muted-foreground">{t('admin.enterpriseDeals.expectedAmount', { amount: money(paymentDeal?.total_contract_value_sar) })}</p></div><div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.amountReceived')} *</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={(event) => setPaymentForm((form) => ({ ...form, amount: event.target.value }))} /></div><div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.bankTransferReference')} *</Label><Input value={paymentForm.reference} onChange={(event) => setPaymentForm((form) => ({ ...form, reference: event.target.value }))} /></div><div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.receivedAt')} *</Label><Input type="datetime-local" value={paymentForm.received_at} onChange={(event) => setPaymentForm((form) => ({ ...form, received_at: event.target.value }))} /></div><div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.confirmationReason')} *</Label><Textarea value={paymentForm.reason} onChange={(event) => setPaymentForm((form) => ({ ...form, reason: event.target.value }))} placeholder={t('admin.enterpriseDeals.confirmationReasonPlaceholder')} /></div><div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /><p>{t('admin.enterpriseDeals.financialApprovalWarning')}</p></div></div><DialogFooter><Button variant="outline" onClick={() => setPaymentDeal(null)}>{t('admin.enterpriseDeals.cancel')}</Button><Button onClick={() => confirmPayment.mutate()} disabled={confirmPayment.isPending || !paymentForm.reference.trim() || !paymentForm.received_at || paymentForm.reason.trim().length < 12}>{confirmPayment.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{t('admin.enterpriseDeals.confirmTransfer')}</Button></DialogFooter></DialogContent>
      </Dialog>

      <Dialog open={!!action} onOpenChange={(open) => !open && setAction(null)}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>{action?.type === 'activate' ? t('admin.enterpriseDeals.activateEnterpriseContract') : t('admin.enterpriseDeals.deactivateEnterpriseContract')}</DialogTitle></DialogHeader><div className="space-y-4 py-2"><div className="flex gap-3 rounded-lg border p-3">{action?.type === 'activate' ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-destructive" />}<div><p className="font-medium">{action?.deal.deal_name}</p><p className="text-sm text-muted-foreground">{action?.type === 'activate' ? t('admin.enterpriseDeals.activationWarning') : t('admin.enterpriseDeals.deactivationWarning')}</p></div></div><div className="space-y-1.5"><Label>{t('admin.enterpriseDeals.administrativeReason')} *</Label><Textarea value={actionReason} onChange={(event) => setActionReason(event.target.value)} placeholder={t('admin.enterpriseDeals.actionReasonPlaceholder')} /></div></div><DialogFooter><Button variant="outline" onClick={() => setAction(null)}>{t('admin.enterpriseDeals.keepUnchanged')}</Button><Button variant={action?.type === 'cancel' ? 'destructive' : 'default'} onClick={() => lifecycleMutation.mutate()} disabled={lifecycleMutation.isPending || actionReason.trim().length < 12}>{lifecycleMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}{action?.type === 'activate' ? t('admin.enterpriseDeals.activateContract') : t('admin.enterpriseDeals.deactivateContract')}</Button></DialogFooter></DialogContent>
      </Dialog>
    </div>
  );
}
